import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useWebsite } from '@/context/WebsiteContext';

const WebsiteSelector: React.FC = () => {
  const { websites, selectedWebsite, setSelectedWebsite, hasWebsites } = useWebsite();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  if (!hasWebsites) {
    return null;
  }

  const displayUrl = selectedWebsite?.url || '';

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 bg-white border border-border rounded h-10 px-4 hover:opacity-80 transition-opacity"
      >
        <span className="font-mono font-medium text-xs tracking-[0.6px] uppercase text-foreground max-w-[280px] truncate">
          {displayUrl}
        </span>
        <ChevronDown className={`w-3 h-3 text-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-2 bg-white border border-border rounded shadow-lg z-50 min-w-full py-1">
          {websites.map((website) => (
            <button
              key={website.id}
              onClick={() => {
                setSelectedWebsite(website);
                setIsOpen(false);
              }}
              className={`w-full text-left px-4 py-2.5 font-mono font-medium text-xs tracking-[0.6px] uppercase transition-colors ${
                selectedWebsite?.id === website.id
                  ? 'bg-secondary text-foreground'
                  : 'text-foreground hover:bg-secondary/50'
              }`}
            >
              {website.url}
            </button>
          ))}
          <div className="border-t border-border mt-1 pt-1">
            <button
              onClick={() => {
                setIsOpen(false);
                navigate('/');
              }}
              className="w-full text-left px-4 py-2.5 font-mono font-medium text-xs tracking-[0.6px] uppercase text-primary hover:bg-secondary/50 transition-colors flex items-center gap-2"
            >
              <Plus className="w-3 h-3" />
              Add Website
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebsiteSelector;
